import React, { useState, useEffect } from 'react';
import {
    Link, useLocation, useNavigate, useParams,
} from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { authManager } from '../utils/auth';
import { isNonMmlAdmin } from '../utils/roles';
import MemberSearchField from '../Components/MemberSearchField';
import { ReceiptDetails, withPopulatedBase } from '../Components/ReceiptDetails';
import {
    availableFundingSources, formatEarnPercent, FUNDING_SOURCES, NOT_COMMISSARY_LABEL,
} from '../utils/loyalty';
import { isTiffFile, useTiffPreview } from '../utils/tiffPreview';

const Base_Url = process.env.REACT_APP_API_BASE_URL;

// Must match somo-circle1-api's ACCEPTED_FILE_TYPES — same convention as
// UploadLoyaltyReceipt.js and the member-facing upload. HEIC/HEIF is
// converted to JPEG server-side (somo-circle1-api#667); TIFF is read by
// Textract natively — neither needs to be blocked client-side.
const ACCEPTED_FILE_TYPES = ['image/jpeg', 'image/png', 'application/pdf', 'image/heic', 'image/heif', 'image/tiff'];
const MAX_FILE_SIZE_BYTES = 20 * 1024 * 1024;

// Real-world gap (somo-circle1-membership-frontend#608): Windows has no
// built-in MIME registration for HEIC unless the optional "HEIF Image
// Extensions" codec pack is installed, and Chromium derives File.type from
// the OS's registered type-by-extension mapping on Windows — so an admin
// picking a genuine .heic file via the browser's file input commonly gets
// file.type === '' there, failing ACCEPTED_FILE_TYPES.includes() even
// though the file is perfectly valid. Same class of fix as the API's own
// resolveAttachmentContentType (loyalty.controller.js /
// lambda-functions/loyalty-center/index.mjs) for a mail client mislabeling
// a HEIC attachment as application/octet-stream.
const GENERIC_FILE_TYPES = new Set(['', 'application/octet-stream']);
const EXTENSION_TO_FILE_TYPE = {
    heic: 'image/heic',
    heif: 'image/heif',
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    png: 'image/png',
    pdf: 'application/pdf',
    tif: 'image/tiff',
    tiff: 'image/tiff',
};

// Resolves a generic/empty declared file.type to the real one, inferred
// from the filename extension. Returns the original type unchanged when
// it's already something specific — never overrides a legitimate, explicit
// browser-reported type, only fills in for the generic/empty case.
const resolveFileType = (file) => {
    if (!GENERIC_FILE_TYPES.has(file.type)) return file.type;
    const extension = (file.name || '').split('.').pop()?.toLowerCase();
    return EXTENSION_TO_FILE_TYPE[extension] || file.type;
};

// Mirrors adminEditReceipt()'s own restriction — an accepted/rejected/
// processing receipt can't be edited (accepted already booked real points
// with no ledger reversal path yet), so this screen matches that instead of
// letting the admin fill out a form that will just 409 on save.
const EDITABLE_STATUSES = ['submitted', 'needs_resubmission'];

// Mirrors LoyaltyReceipts.js's own badge/label maps (kept local here rather
// than shared — a small, stable enum unlikely to drift, and this screen
// can't currently be covered by that file's own Jest suite to catch a
// refactor regression there — see repo-wide react-router-dom/Jest note).
const STATUS_META = {
    submitted: { badge: 'badge-info', label: 'Submitted' },
    needs_resubmission: { badge: 'badge-warning', label: 'Needs a better photo' },
};
// Full set (including terminal states) — only needed for the duplicate-of
// receipt's status, which can be any status, not just the editable ones above.
const STATUS_LABEL = {
    submitted: 'Submitted',
    accepted: 'Accepted',
    rejected: 'Rejected',
    needs_resubmission: 'Needs a better photo',
};
const SOURCE_META = {
    upload: { badge: 'badge-light', label: 'Upload' },
    email: { badge: 'badge-info', label: 'Email' },
    admin_upload: { badge: 'badge-secondary', label: 'Admin Upload' },
};
// The loyalty-center Lambda's file name for a receipt read from a forwarded
// email's body (somo-circle1-api#823). Same constant as LoyaltyReceipts.js.
const EMAIL_BODY_RECEIPT_FILENAME = 'email-body.pdf';
// 'conversion_failed' (somo-circle1-api#667): a HEIC/HEIF receipt whose
// server-side JPEG conversion itself failed, so OCR never ran — distinct
// from 'unsupported' so an admin can tell "we tried and it didn't work"
// apart from "this was never going to be OCR'd." Without an entry here it
// falls through to ocrStatusMeta's own fallback and shows the raw enum
// value ("conversion_failed") instead of a real label.
const OCR_STATUS_META = {
    completed: { badge: 'badge-success', label: 'Completed' },
    not_commissary: { badge: 'badge-warning', label: NOT_COMMISSARY_LABEL },
    failed: { badge: 'badge-danger', label: 'Failed' },
    unsupported: { badge: 'badge-secondary', label: 'Unsupported file type' },
    conversion_failed: { badge: 'badge-danger', label: 'Conversion failed' },
    pending: { badge: 'badge-secondary', label: 'Pending' },
};
const ocrStatusMeta = (status) => OCR_STATUS_META[status] || { badge: 'badge-light', label: status || 'Unknown' };

// Always called against fileUrl/s3Key, never fileName — somo-circle1-api#667:
// a successfully-converted HEIC/HEIF receipt keeps its ORIGINAL fileName
// (e.g. "receipt.heic") for display/audit even after s3Key/fileUrl are
// repointed to the converted .jpg. fileExtension works fine against a full
// URL too — .split('.').pop() grabs the last dot-separated segment
// regardless of other dots earlier in the domain.
const IMAGE_EXTENSIONS = new Set(['jpg', 'jpeg', 'png']);
const fileExtension = (name) => (name || '').split('.').pop()?.toLowerCase();
const isImageFile = (name) => IMAGE_EXTENSIONS.has(fileExtension(name));

const formatDollars = (amount) => `$${Number(amount).toFixed(2)}`;

// Trims FUNDING_SOURCES' full dropdown label (e.g. "Our P&L (standard 5%
// back, subject to the annual cap)") down to just the name, for the
// compact version-history rows below — the full explanation belongs on the
// dropdown, not repeated per row.
const shortFundingSourceLabel = (value) => {
    const match = FUNDING_SOURCES.find((f) => f.value === value);
    return match ? match.label.split(' (')[0] : value;
};

// Matches LoyaltyReceipts.js's own convention — opaque, camera-uploaded
// filenames are long tokens that add noise, not information, to the
// Version History list below.
const truncateMiddle = (str, headLen = 12, tailLen = 8) => {
    if (!str || str.length <= headLen + tailLen + 3) return str;
    return `${str.slice(0, headLen)}...${str.slice(-tailLen)}`;
};

// capPeriod comes back as a date within the cap year — labels the cap
// figures with the actual year they cover. Falls back to today's year if
// capPeriod is missing (somo-circle1-api#686 — switched from a monthly cap
// window to an annual one). Read in UTC, not the viewer's local zone: the
// backend's cap-year boundary is UTC-anchored (firstOfYearUTC-style), so a
// capPeriod in the first few hours of Jan 1 UTC would otherwise display the
// prior year for any viewer in a timezone behind UTC (e.g. the US).
const capPeriodYearLabel = (capPeriod) => {
    const date = capPeriod && !Number.isNaN(new Date(capPeriod).getTime()) ? new Date(capPeriod) : new Date();
    return date.toLocaleString('en-US', { year: 'numeric', timeZone: 'UTC' });
};

const duplicateOriginLabel = (original) => {
    if (!original) return null;
    const name = [original.userId?.name, original.userId?.lastName].filter(Boolean).join(' ') || 'another member';
    const when = original.createdAt ? new Date(original.createdAt).toLocaleDateString() : '';
    return `submitted by ${name}${when ? ` on ${when}` : ''}`;
};

const ocrUnavailableReason = (ocrStatus) => {
    if (ocrStatus === 'not_commissary') {
        return "no line on this receipt says \"COMMISSARY\" — choose On base for an Exchange receipt, Off base for any other store";
    }
    if (ocrStatus === 'failed') return "OCR couldn't read a total on this receipt";
    if (ocrStatus === 'unsupported') return "OCR doesn't support this file type";
    if (ocrStatus === 'conversion_failed') return "This HEIC/HEIF file couldn't be converted, so OCR never ran";
    if (ocrStatus === 'pending') return "OCR hasn't run on this receipt yet";
    return 'This receipt predates OCR support';
};

const OCR_HAS_USABLE_TOTAL = ['completed', 'not_commissary'];

// somo-circle1-api#796: the "Bought" picker's default. OCR matched a
// Commissary: on base. Anything else (Exchange, off base, unreadable) is
// left blank for the reviewer to pick from the image.
const defaultEarnLocation = (r) => r.earnLocation || (r.ocrStatus === 'completed' ? 'on_base' : '');

const EditLoyaltyReceipt = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    // The queue page/filters the admin was on before opening this receipt
    // (LoyaltyReceipts.js sets this via the "Edit / Review" link's `state`)
    // — carried back through so Approve/Reject/Back doesn't dump them back
    // onto page 1 of the default 'submitted' filter.
    const queueReturnState = location.state?.from ? { restoreFilters: location.state.from } : undefined;

    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState('');
    const [receipt, setReceipt] = useState(null);
    const [previewImage, setPreviewImage] = useState(null); // { url, label, displaySrc? } | null

    // Browsers can't render TIFF in an <img>, and the API keeps TIFF receipts in
    // that format on purpose (Textract OCRs it natively — unlike HEIC/HEIF,
    // which the API converts to JPEG). So a .tiff receipt is decoded to a PNG
    // client-side, once per URL: the sidebar preview + this-submission
    // thumbnail (receiptTiff), the flagged duplicate's own thumbnail
    // (duplicateTiff), and the full-size modal (modalTiff, only when the click
    // didn't already hand it a decoded displaySrc). Passthrough for non-TIFF.
    const receiptTiff = useTiffPreview(receipt?.fileUrl);
    const duplicateTiff = useTiffPreview(receipt?.duplicateOfReceiptId?.fileUrl);
    const modalTiff = useTiffPreview(
        previewImage && !previewImage.displaySrc ? previewImage.url : null,
    );

    // A 90×90 thumbnail in the "Possible Duplicate" card. Mirrors the sidebar
    // preview's TIFF handling: decoded image when ready, spinner while
    // decoding, file-type icon on failure — never a raw <img src=".tiff">.
    const renderDuplicateThumb = ({
        url, tiff, alt, borderColor, label,
    }) => {
        const decoding = isTiffFile(url);
        const src = decoding ? (tiff.status === 'ready' ? tiff.src : null) : url;
        const boxStyle = {
            width: 90, height: 90, borderRadius: 6, border: `2px solid var(--${borderColor})`,
        };
        return (
            <button
                type="button"
                className="btn p-0 border-0 bg-transparent"
                onClick={() => setPreviewImage({
                    url,
                    label,
                    displaySrc: decoding && tiff.status === 'ready' ? tiff.src : undefined,
                })}
            >
                {src ? (
                    <img src={src} alt={alt} style={{ ...boxStyle, objectFit: 'cover' }} />
                ) : (
                    <span className="d-flex align-items-center justify-content-center" style={boxStyle}>
                        {decoding && tiff.status !== 'error' ? (
                            <span className="spinner-border spinner-border-sm text-secondary" role="status" aria-hidden="true" />
                        ) : (
                            <i className="fa-solid fa-file-image text-muted" style={{ fontSize: '1.5rem' }} />
                        )}
                    </span>
                )}
            </button>
        );
    };

    const [selectedMember, setSelectedMember] = useState(null);
    const [selectedFile, setSelectedFile] = useState(null);
    const [fileError, setFileError] = useState('');

    const [grossTotalInput, setGrossTotalInput] = useState('');
    const [snapTotalInput, setSnapTotalInput] = useState('');
    const [fundingSourceInput, setFundingSourceInput] = useState('');
    const [amountsError, setAmountsError] = useState('');
    const [enabledFundingSources, setEnabledFundingSources] = useState(null);
    // somo-circle1-api#796: on base (Commissary/Exchange) or off base, which
    // picks the earn rate. Blank until the reviewer chooses, unless OCR
    // matched a Commissary — see the receipt load below.
    const [earnLocationInput, setEarnLocationInput] = useState('');
    // Earn-side config for the rate labels and the points estimate. Falls
    // back to the API's defaults if the config read fails.
    const [earnConfig, setEarnConfig] = useState({
        earnPercentage: 0.05, offBaseEarnPercentage: 0.01, earnRatePointsPerDollar: 10,
    });
    // False until GET /admin/loyalty/config returns the real rates, so the
    // picker doesn't show the fallback percentages as if they were current.
    const [earnConfigLoaded, setEarnConfigLoaded] = useState(false);
    // A file replacement, OCR retry or version restore can change what OCR
    // says about the store, which is what the picker's default rests on. When
    // it does, reset the pick to the new default so it can't silently stay
    // "on base" for a receipt OCR no longer matches to a Commissary. A pick
    // survives saves that leave ocrStatus alone (e.g. a member reassignment).
    const syncEarnLocation = (prevReceipt, nextReceipt) => {
        if (prevReceipt?.ocrStatus !== nextReceipt.ocrStatus) {
            setEarnLocationInput(defaultEarnLocation(nextReceipt));
        }
    };
    const fundingSourceOptions = availableFundingSources(enabledFundingSources);

    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState('');
    const [lastSaved, setLastSaved] = useState(null);
    const [lastSavedLabel, setLastSavedLabel] = useState('Receipt updated.');

    const [retryingOcr, setRetryingOcr] = useState(false);
    const [retryError, setRetryError] = useState('');

    // Restore-then-decide version history — which past version (if any) is
    // currently being restored, so only that row's button shows "Restoring…"
    // while the others stay clickable-looking but are still disabled below.
    const [restoringVersionId, setRestoringVersionId] = useState(null);
    const [restoreError, setRestoreError] = useState('');

    // ---- Decision (Approve/Reject) — absorbed from the old Review modal ----
    const [reviewType, setReviewType] = useState('approve');
    const [reason, setReason] = useState('');
    const [capStatus, setCapStatus] = useState(null);
    const [capLoading, setCapLoading] = useState(false);
    const [capError, setCapError] = useState(false);
    // General manual-award override — not limited to the cap-exceeded case,
    // works for any funding source, up or down (somo-circle1-api-admin#153).
    const [overrideCap, setOverrideCap] = useState(false);
    const [overrideReason, setOverrideReason] = useState('');
    // Admin-typed points to award, in whole points (converted to thousandths
    // on submit) — pre-filled from rawPointsEstimate when the override is
    // opened, but freely editable.
    const [overridePoints, setOverridePoints] = useState('');
    const [deciding, setDeciding] = useState(false);
    const [decideError, setDecideError] = useState('');
    // Stored OCR details card (somo-circle1-api#777) — CircleOne-only, same
    // rule as the queue's Details button: its line-items endpoint is gated by
    // requireNonMmlAdmin. A failed lookup hides the card.
    const [showStoredDetails, setShowStoredDetails] = useState(false);

    useEffect(() => {
        let cancelled = false;
        authManager.validateUser()
            .then((user) => {
                if (!cancelled) setShowStoredDetails(isNonMmlAdmin(user));
            })
            .catch(() => {});
        return () => { cancelled = true; };
    }, []);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            setLoadError('');
            try {
                const res = await authManager.authenticatedFetch(`${Base_Url}/admin/loyalty/receipts/${id}`);
                const data = await res.json();
                if (!res.ok) {
                    throw new Error(data?.message || 'Could not load this receipt.');
                }
                if (cancelled) return;
                setReceipt(data.data);
                setSelectedMember(data.data.userId || null);
                setGrossTotalInput(data.data.grossTotal != null ? String(data.data.grossTotal) : '');
                setSnapTotalInput(data.data.snapTenderedTotal != null ? String(data.data.snapTenderedTotal) : '');
                // Defaults to 'our_pl' rather than blank — same as the old Review
                // modal's own default — so the cap preview and Approve are usable
                // immediately without an extra selection step; the reconcile
                // effect below snaps this to the first actually-enabled source
                // once config loads, same as the old modal did.
                setFundingSourceInput(data.data.fundingSource || 'our_pl');
                setEarnLocationInput(defaultEarnLocation(data.data));
            } catch (err) {
                if (!cancelled) setLoadError(err.message || 'Could not load this receipt.');
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [id]);

    // Best-effort: which funding sources to offer, same convention (and same
    // GET /admin/loyalty/config read) the old Review modal used — a config
    // fetch failure just falls back to the full canonical set.
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const res = await authManager.authenticatedFetch(`${Base_Url}/admin/loyalty/config`);
                const data = await res.json();
                if (!cancelled && res.ok && Array.isArray(data.data?.enabledFundingSources)) {
                    setEnabledFundingSources(data.data.enabledFundingSources);
                }
                if (!cancelled && res.ok && data.data) {
                    setEarnConfigLoaded(true);
                    setEarnConfig((prev) => ({
                        earnPercentage: Number.isFinite(data.data.earnPercentage) ? data.data.earnPercentage : prev.earnPercentage,
                        offBaseEarnPercentage: Number.isFinite(data.data.offBaseEarnPercentage)
                            ? data.data.offBaseEarnPercentage : prev.offBaseEarnPercentage,
                        earnRatePointsPerDollar: Number.isFinite(data.data.earnRatePointsPerDollar)
                            ? data.data.earnRatePointsPerDollar : prev.earnRatePointsPerDollar,
                    }));
                }
            } catch (err) {
                // Falls back to availableFundingSources(null) — the full set.
            }
        })();
        return () => { cancelled = true; };
    }, []);

    // Snap the funding-source selection back to a valid one if the enabled
    // set loads (or changes) while it's pointed at something no longer
    // offered — same reconcile the old modal did.
    useEffect(() => {
        if (fundingSourceOptions.length > 0 && fundingSourceInput
            && !fundingSourceOptions.some((f) => f.value === fundingSourceInput)) {
            setFundingSourceInput(fundingSourceOptions[0].value);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [enabledFundingSources]);

    // Annual cap preview: only meaningful for our_pl (client_promo/b5g50 are
    // uncapped by design) and only while the Approve outcome is selected.
    // Point-in-time read for display only — approve() itself is always the
    // authority on the real, final numbers.
    useEffect(() => {
        if (!receipt || reviewType !== 'approve' || fundingSourceInput !== 'our_pl') {
            setCapStatus(null);
            setCapLoading(false);
            setCapError(false);
            return undefined;
        }
        let cancelled = false;
        setCapLoading(true);
        setCapError(false);
        (async () => {
            try {
                const res = await authManager.authenticatedFetch(`${Base_Url}/admin/loyalty/receipts/${id}/cap-status`);
                const data = await res.json();
                const valid = data.status === 'success'
                    && Number.isFinite(data.data?.cap?.points)
                    && Number.isFinite(data.data?.consumed?.points)
                    && Number.isFinite(data.data?.remaining?.points);
                if (!cancelled) {
                    setCapStatus(valid ? data.data : null);
                    setCapError(!valid);
                }
            } catch (err) {
                if (!cancelled) {
                    setCapStatus(null);
                    setCapError(true);
                }
            } finally {
                if (!cancelled) setCapLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [receipt, id, reviewType, fundingSourceInput]);

    const handleMemberSelect = (member) => {
        setSelectedMember(member);
        setLastSaved(null);
    };

    const handleFileSelected = (e) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (!file) return;

        setLastSaved(null);
        const resolvedType = resolveFileType(file);
        if (!ACCEPTED_FILE_TYPES.includes(resolvedType)) {
            setFileError('Please choose a JPEG, PNG, PDF, TIFF, HEIC, or HEIF file.');
            setSelectedFile(null);
            return;
        }
        if (file.size > MAX_FILE_SIZE_BYTES) {
            setFileError('That file is larger than 20MB. Please choose a smaller photo or PDF.');
            setSelectedFile(null);
            return;
        }
        setFileError('');
        // Store the file with its CORRECTED type from here on — the save
        // handler's own selectedFile.type reads (the presign request body
        // and the actual multipart upload's Content-Type — S3's presigned
        // POST policy requires those to exactly match, and FormData derives
        // the upload's header from the File/Blob's own .type) are already
        // right without that handler needing to know anything was resolved.
        setSelectedFile(resolvedType === file.type ? file : new File([file], file.name, { type: resolvedType }));
    };

    const clearSelectedFile = () => {
        setSelectedFile(null);
        setFileError('');
    };

    // Mirrors adminEditReceipt()'s own validation so a bad value is caught
    // before the round trip, not just after a 400 comes back. Returns the
    // parsed { grossTotal?, snapTenderedTotal?, fundingSource? } payload
    // fields (only the ones the admin actually filled in) or null if invalid.
    const parseAmountsInput = () => {
        setAmountsError('');
        const payload = {};

        if (grossTotalInput.trim() !== '') {
            const value = Number(grossTotalInput);
            if (!(Number.isFinite(value) && value >= 0)) {
                setAmountsError('Receipt total must be a non-negative number.');
                return null;
            }
            payload.grossTotal = value;
        }
        if (snapTotalInput.trim() !== '') {
            const value = Number(snapTotalInput);
            if (!(Number.isFinite(value) && value >= 0)) {
                setAmountsError('SNAP/EBT amount must be a non-negative number.');
                return null;
            }
            payload.snapTenderedTotal = value;
        }
        if (payload.grossTotal !== undefined && payload.snapTenderedTotal !== undefined
            && payload.snapTenderedTotal > payload.grossTotal) {
            setAmountsError('SNAP/EBT amount cannot exceed the receipt total.');
            return null;
        }
        if (fundingSourceInput) payload.fundingSource = fundingSourceInput;

        return payload;
    };

    const handleSave = async () => {
        if (!selectedMember) return;

        const amountsPayload = parseAmountsInput();
        if (!amountsPayload) return;

        setSaveError('');
        setRestoreError('');
        setSaving(true);
        try {
            let key;
            let fileName;

            if (selectedFile) {
                const presignRes = await authManager.authenticatedFetch(
                    `${Base_Url}/admin/loyalty/receipts/presign`,
                    {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            userId: selectedMember._id, fileName: selectedFile.name, fileType: selectedFile.type,
                        }),
                    },
                );
                const presignData = await presignRes.json();
                if (!presignRes.ok || !presignData.uploadUrl || !presignData.fields || !presignData.fileUrl) {
                    throw new Error(presignData.message || 'Could not prepare the upload. Please try again.');
                }

                // S3 POST policy requires the 'file' field to be added last —
                // anything appended after it is ignored by S3.
                const formData = new FormData();
                Object.entries(presignData.fields).forEach(([field, value]) => {
                    formData.append(field, value);
                });
                formData.append('file', selectedFile);

                const s3Res = await fetch(presignData.uploadUrl, { method: 'POST', body: formData });
                if (!s3Res.ok) {
                    throw new Error('Upload failed. Please try again.');
                }

                key = presignData.key;
                fileName = selectedFile.name;
            }

            const patchRes = await authManager.authenticatedFetch(
                `${Base_Url}/admin/loyalty/receipts/${id}`,
                {
                    method: 'PATCH',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        userId: selectedMember._id,
                        ...(key ? { key, fileName } : {}),
                        ...amountsPayload,
                    }),
                },
            );
            const patchData = await patchRes.json();
            if (!patchRes.ok) {
                throw new Error(patchData.message || 'Could not save these changes. Please try again.');
            }

            syncEarnLocation(receipt, patchData.data);
            setReceipt((prev) => withPopulatedBase(prev, patchData.data));
            setSelectedMember(patchData.data.userId || selectedMember);
            setSelectedFile(null);
            setGrossTotalInput(patchData.data.grossTotal != null ? String(patchData.data.grossTotal) : '');
            setSnapTotalInput(patchData.data.snapTenderedTotal != null ? String(patchData.data.snapTenderedTotal) : '');
            setFundingSourceInput(patchData.data.fundingSource || '');
            if (patchData.noChange) {
                // Nothing actually changed — surfaced as a warning rather than
                // a success toast, and no result banner (there's no "result"
                // to show), so it doesn't read as if a real edit just landed.
                toast.warning(patchData.message || 'Nothing changed.');
            } else {
                setLastSaved(patchData.data);
                setLastSavedLabel('Receipt updated.');
                toast.success('Receipt updated.');
            }
        } catch (err) {
            setSaveError(err.message || 'Something went wrong. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    // Re-runs OCR against the file already on record — no re-upload needed.
    // Most "OCR failed" rows are a transient Textract/network hiccup rather
    // than an actually bad file, so this is worth trying before assuming
    // the file itself needs replacing.
    const handleRetryOcr = async () => {
        setRetryError('');
        setRestoreError('');
        setLastSaved(null);
        setRetryingOcr(true);
        try {
            const res = await authManager.authenticatedFetch(
                `${Base_Url}/admin/loyalty/receipts/${id}`,
                {
                    method: 'PATCH',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ retryOcr: true }),
                },
            );
            const data = await res.json();
            if (!res.ok) {
                throw new Error(data.message || 'Could not re-check OCR. Please try again.');
            }
            syncEarnLocation(receipt, data.data);
            setReceipt((prev) => withPopulatedBase(prev, data.data));
            setGrossTotalInput(data.data.grossTotal != null ? String(data.data.grossTotal) : '');
            setSnapTotalInput(data.data.snapTenderedTotal != null ? String(data.data.snapTenderedTotal) : '');
            if (data.noChange) {
                toast.warning(data.message || 'OCR came back the same as before — nothing changed.');
            } else {
                setLastSaved(data.data);
                setLastSavedLabel('OCR re-checked.');
                toast.success('OCR re-checked.');
            }
        } catch (err) {
            setRetryError(err.message || 'Could not re-check OCR. Please try again.');
        } finally {
            setRetryingOcr(false);
        }
    };

    // Restore-then-decide: makes a past version's file/OCR/amount data the
    // receipt's current data again. Approve/Reject then act on it exactly
    // like any other edit — no separate "decide against this version" path.
    // Any unsaved replacement file is dropped, since it'd otherwise be
    // ambiguous which file the admin actually means to act on next.
    const handleRestoreVersion = async (versionId) => {
        setRestoreError('');
        setLastSaved(null);
        setRestoringVersionId(versionId);
        try {
            const res = await authManager.authenticatedFetch(
                `${Base_Url}/admin/loyalty/receipts/${id}/restore-version`,
                {
                    method: 'PATCH',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ versionId }),
                },
            );
            const data = await res.json();
            if (!res.ok) {
                throw new Error(data.message || 'Could not restore this version. Please try again.');
            }
            syncEarnLocation(receipt, data.data);
            setReceipt((prev) => withPopulatedBase(prev, data.data));
            setSelectedFile(null);
            setGrossTotalInput(data.data.grossTotal != null ? String(data.data.grossTotal) : '');
            setSnapTotalInput(data.data.snapTenderedTotal != null ? String(data.data.snapTenderedTotal) : '');
            setFundingSourceInput(data.data.fundingSource || '');
            setLastSaved(data.data);
            setLastSavedLabel('Version restored.');
            toast.success('Version restored.');
        } catch (err) {
            setRestoreError(err.message || 'Could not restore this version. Please try again.');
        } finally {
            setRestoringVersionId(null);
        }
    };

    // Dollars in the UI, cents on the wire — matches loyaltyPointsCalculator's
    // grossCents/snapTenderedCents contract in somo-circle1-api.
    const toCents = (dollars) => Math.round(Number(dollars) * 100);
    // Whole points in the UI, thousandths on the wire — matches
    // computeEarnPoints'/bookLoyaltyEarn's thousandths-of-a-point contract.
    const toThousandths = (points) => Math.round(Number(points) * 1000);

    // Same formula as the server's computeEarnPoints (earn rate * pts/$1, e.g.
    // 5% * 10 = 0.5 pts/eligible-dollar on base) — client-side estimate only;
    // approve() is the real authority. The rate follows the on/off-base pick
    // (#796); with nothing picked yet it estimates at the on-base rate.
    const earnPercentFor = (location) => (location === 'off_base'
        ? earnConfig.offBaseEarnPercentage
        : earnConfig.earnPercentage);
    // Option suffix, e.g. " (5%)". Left off while the config hasn't loaded,
    // rather than showing the fallback rates as if they were current.
    const ratePart = (fraction) => (earnConfigLoaded ? ` (${formatEarnPercent(fraction)})` : '');
    const eligibleDollars = grossTotalInput && Number(grossTotalInput) > 0
        && Number(snapTotalInput || 0) <= Number(grossTotalInput)
        ? Number(grossTotalInput) - Number(snapTotalInput || 0)
        : null;
    const rawPointsEstimate = eligibleDollars != null
        ? eligibleDollars * earnPercentFor(earnLocationInput) * earnConfig.earnRatePointsPerDollar
        : null;
    const wouldExceedCap = fundingSourceInput === 'our_pl' && !!capStatus && rawPointsEstimate != null
        && rawPointsEstimate > capStatus.remaining.points + 1e-9;

    // Approve/Reject only ever sends amounts/funding-source/override fields —
    // a picked replacement file or a member reassignment only takes effect
    // via handleSave's own upload/PATCH call. Block deciding while either is
    // unsaved so it can't be silently discarded (somo-circle1-api-admin#156
    // review).
    const hasUnsavedFileOrMember = !!selectedFile
        || (!!selectedMember && !!receipt?.userId && selectedMember._id !== receipt.userId._id);

    const selectReviewType = (type) => {
        setReviewType(type);
        setReason('');
        setDecideError('');
        setOverrideCap(false);
        setOverrideReason('');
        setOverridePoints('');
    };

    // General manual-award override — available any time an admin approves,
    // not just when the cap is exceeded (somo-circle1-api-admin#153). Opens
    // pre-filled with the formula's own raw estimate so the admin is
    // adjusting a real starting point, not typing from scratch.
    const openOverride = () => {
        setOverrideCap(true);
        if (!overridePoints && rawPointsEstimate != null) {
            setOverridePoints(rawPointsEstimate.toFixed(3));
        }
    };

    const closeOverride = () => {
        setOverrideCap(false);
        setOverridePoints('');
        setOverrideReason('');
    };

    const handleDecide = async () => {
        if (!receipt) return;
        setDecideError('');
        setRestoreError('');

        if (hasUnsavedFileOrMember) {
            setDecideError('Save the replacement file / member change first — Approve and Reject don\'t include unsaved edits.');
            return;
        }

        // Defense in depth to match the button's own disabled state: a
        // restore in flight hasn't updated grossTotalInput/snapTotalInput/
        // fundingSourceInput yet, so deciding right now would book points
        // against whatever amounts were showing BEFORE the restore, not the
        // version the admin just told the system to make current.
        if (restoringVersionId) {
            setDecideError('Wait for the version restore to finish before approving or rejecting.');
            return;
        }

        if (reviewType === 'reject') {
            if (!reason.trim()) {
                setDecideError('A reason is required.');
                return;
            }
        } else {
            const grossCents = toCents(grossTotalInput);
            const snapTenderedCents = toCents(snapTotalInput || '0');
            if (!grossTotalInput || !Number.isFinite(grossCents) || grossCents <= 0) {
                setDecideError('Enter the receipt total (must be greater than $0).');
                return;
            }
            if (!Number.isFinite(snapTenderedCents) || snapTenderedCents < 0 || snapTenderedCents > grossCents) {
                setDecideError('SNAP-tendered amount must be between $0 and the receipt total.');
                return;
            }
            if (!fundingSourceOptions.some((f) => f.value === fundingSourceInput)) {
                setDecideError('Choose a funding source — the one selected is no longer available.');
                return;
            }
            if (!earnLocationInput) {
                setDecideError('Choose where it was bought: on base or off base.');
                return;
            }
            if (overrideCap && !overrideReason.trim()) {
                setDecideError('A reason is required to override the awarded points.');
                return;
            }
            const overridePointsThousandths = toThousandths(overridePoints);
            if (overrideCap && (!overridePoints || !Number.isFinite(overridePointsThousandths) || overridePointsThousandths < 0)) {
                setDecideError('Enter the points to award (0 or greater).');
                return;
            }
        }

        setDeciding(true);
        try {
            let body;
            if (reviewType === 'reject') {
                body = JSON.stringify({ reason: reason.trim() });
            } else {
                body = JSON.stringify({
                    grossCents: toCents(grossTotalInput),
                    snapTenderedCents: toCents(snapTotalInput || '0'),
                    fundingSource: fundingSourceInput,
                    earnLocation: earnLocationInput,
                    // General manual-award override — not limited to the
                    // cap-exceeded case, so this is sent whenever the admin
                    // checked the override control, regardless of
                    // wouldExceedCap or fundingSource.
                    ...(overrideCap ? {
                        overrideCap: true,
                        overrideReason: overrideReason.trim(),
                        overridePoints: toThousandths(overridePoints),
                    } : {}),
                });
            }
            const res = await authManager.authenticatedFetch(
                `${Base_Url}/admin/loyalty/receipts/${id}/${reviewType}`,
                { method: 'POST', headers: { 'Content-Type': 'application/json' }, body },
            );
            const data = await res.json();
            if (data.status !== 'success') {
                throw new Error(data.message || 'That action failed.');
            }
            toast.success(data.message || 'Done.');
            navigate('/loyaltyReceipts', { state: queueReturnState });
        } catch (err) {
            setDecideError(err.message || 'That action failed.');
        } finally {
            setDeciding(false);
        }
    };

    const renderResultAlert = () => {
        if (!lastSaved) return null;
        return (
            <div className="alert alert-success mb-4">
                <strong>{lastSavedLabel}</strong>
                {lastSaved.ocrStatus === 'completed' && lastSaved.grossTotal != null && (
                    <div className="mt-1">OCR read a total of <strong>{formatDollars(lastSaved.grossTotal)}</strong>.</div>
                )}
                {lastSaved.ocrStatus === 'failed' && <div className="mt-1">OCR could not read the file — it will need manual review.</div>}
                {lastSaved.ocrStatus === 'not_commissary' && <div className="mt-1">OCR did not detect a commissary line item — check whether it&apos;s an Exchange (on base) or off-base receipt.</div>}
                {lastSaved.ocrStatus === 'unsupported' && <div className="mt-1">This file type isn&apos;t supported by OCR — it will need manual review.</div>}
                {lastSaved.ocrStatus === 'conversion_failed' && (
                    <div className="mt-1">This HEIC/HEIF file couldn&apos;t be converted, so OCR never ran — it will need manual review.</div>
                )}
                {lastSaved.possibleDuplicate && (
                    <div className="text-warning mt-1">
                        <i className="fa-solid fa-triangle-exclamation" /> Flagged as a possible duplicate of an existing receipt.
                    </div>
                )}
            </div>
        );
    };

    const renderPreviewSidebar = () => (
        <div className="col-lg-4">
            <div className="card card-primary card-outline">
                <div className="card-header">
                    <h5 className="card-title mb-0">Receipt Preview</h5>
                </div>
                <div className="card-body text-center">
                    {isImageFile(receipt.fileUrl || receipt.s3Key) ? (
                        <img
                            src={receipt.fileUrl}
                            alt={receipt.fileName || 'Receipt'}
                            className="img-fluid rounded"
                            style={{
                                maxHeight: '260px', cursor: 'pointer', border: '1px solid var(--grey2)',
                            }}
                            onClick={() => setPreviewImage({ url: receipt.fileUrl, label: receipt.fileName || 'Receipt' })}
                        />
                    ) : isTiffFile(receipt.fileUrl || receipt.s3Key) ? (
                        receiptTiff.status === 'ready' ? (
                            <img
                                src={receiptTiff.src}
                                alt={receipt.fileName || 'Receipt'}
                                className="img-fluid rounded"
                                style={{
                                    maxHeight: '260px', cursor: 'pointer', border: '1px solid var(--grey2)',
                                }}
                                onClick={() => setPreviewImage({
                                    url: receipt.fileUrl,
                                    label: receipt.fileName || 'Receipt',
                                    displaySrc: receiptTiff.src,
                                })}
                            />
                        ) : (
                            <div
                                className="d-flex flex-column align-items-center justify-content-center rounded"
                                style={{
                                    height: '180px', border: '1px dashed var(--grey2)', cursor: 'pointer',
                                }}
                                onClick={() => window.open(receipt.fileUrl, '_blank', 'noopener,noreferrer')}
                            >
                                {receiptTiff.status === 'error' ? (
                                    <>
                                        <i className="fa-solid fa-file-image" style={{ fontSize: '3rem', color: 'var(--grey2)' }} />
                                        <small className="text-muted mt-2">TIFF file — click to open</small>
                                    </>
                                ) : (
                                    <>
                                        <div className="spinner-border text-primary" role="status">
                                            <span className="sr-only">Loading preview...</span>
                                        </div>
                                        <small className="text-muted mt-2">Rendering TIFF…</small>
                                    </>
                                )}
                            </div>
                        )
                    ) : (
                        <div
                            className="d-flex flex-column align-items-center justify-content-center rounded"
                            style={{
                                height: '180px', border: '1px dashed var(--grey2)', cursor: 'pointer',
                            }}
                            onClick={() => window.open(receipt.fileUrl, '_blank', 'noopener,noreferrer')}
                        >
                            <i className="fa-solid fa-file-pdf" style={{ fontSize: '3rem', color: '#dc3545' }} />
                            <small className="text-muted mt-2">PDF file — click to open</small>
                        </div>
                    )}
                    <div className="mt-2 text-truncate" title={receipt.fileName || receipt.fileUrl}>
                        <small className="text-muted">{receipt.fileName || receipt.fileUrl}</small>
                    </div>
                    <a href={receipt.fileUrl} target="_blank" rel="noopener noreferrer" className="d-block mt-1">
                        Open original <i className="fa-solid fa-up-right-from-square" style={{ fontSize: '0.75rem' }} />
                    </a>
                </div>
            </div>

            {receipt.possibleDuplicate && (
                <div className="card card-danger card-outline">
                    <div className="card-header">
                        <h5 className="card-title mb-0 text-danger"><i className="fa-solid fa-triangle-exclamation" /> Possible Duplicate</h5>
                    </div>
                    <div className="card-body">
                        <p className="mb-2">
                            A receipt with the same store, total, and receipt details already exists
                            {receipt.duplicateOfReceiptId && (
                                <> ({duplicateOriginLabel(receipt.duplicateOfReceiptId)}, status:{' '}
                                    {STATUS_LABEL[receipt.duplicateOfReceiptId.status] || receipt.duplicateOfReceiptId.status})</>
                            )}.
                        </p>
                        {receipt.duplicateOfReceiptId && (
                            <div className="d-flex justify-content-center" style={{ gap: '16px' }}>
                                <div className="text-center">
                                    {renderDuplicateThumb({
                                        url: receipt.fileUrl,
                                        tiff: receiptTiff,
                                        alt: 'This submission',
                                        borderColor: 'primary',
                                        label: 'This submission',
                                    })}
                                    <div><small className="text-muted">This submission</small></div>
                                </div>
                                <div className="text-center">
                                    {renderDuplicateThumb({
                                        url: receipt.duplicateOfReceiptId.fileUrl,
                                        tiff: duplicateTiff,
                                        alt: 'Original submission',
                                        borderColor: 'danger',
                                        label: receipt.duplicateOfReceiptId.fileName || 'Original receipt',
                                    })}
                                    <div><small className="text-muted">Original</small></div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}

            <div className="card card-primary card-outline">
                <div className="card-header">
                    <h5 className="card-title mb-0">Status</h5>
                </div>
                <div className="card-body">
                    <table className="table table-sm table-borderless mb-0">
                        <tbody>
                            <tr>
                                <th style={{ width: '110px' }}>Status</th>
                                <td>
                                    <span className={`badge ${(STATUS_META[receipt.status] || {}).badge || 'badge-secondary'}`}>
                                        {(STATUS_META[receipt.status] || {}).label || receipt.status}
                                    </span>
                                </td>
                            </tr>
                            <tr>
                                <th>Source</th>
                                <td>
                                    <span className={`badge ${(SOURCE_META[receipt.source] || SOURCE_META.upload).badge}`}>
                                        {(SOURCE_META[receipt.source] || SOURCE_META.upload).label}
                                    </span>
                                    {receipt.fileName === EMAIL_BODY_RECEIPT_FILENAME && (
                                        <div data-testid="email-body-receipt-note">
                                            <span className="badge badge-warning">From email body</span>
                                            <br />
                                            <small className="text-muted">
                                                Read from a forwarded order email, not a photo. Check the order,
                                                and the member&apos;s other receipts for the same order.
                                            </small>
                                        </div>
                                    )}
                                </td>
                            </tr>
                            <tr>
                                <th>Submitted</th>
                                <td>{new Date(receipt.createdAt).toLocaleString()}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>

            <div className="card card-primary card-outline">
                <div className="card-header">
                    <h5 className="card-title mb-0">OCR</h5>
                </div>
                <div className="card-body text-center">
                    <span className={`badge ${ocrStatusMeta(receipt.ocrStatus).badge} px-3 py-2 mb-2`} style={{ fontSize: '0.9rem' }}>
                        {ocrStatusMeta(receipt.ocrStatus).label}
                    </span>
                    {receipt.ocrStatus === 'completed' && receipt.grossTotal != null && (
                        <div className="mb-2">Total: <strong>{formatDollars(receipt.grossTotal)}</strong></div>
                    )}
                    <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary d-block mx-auto"
                        onClick={handleRetryOcr}
                        disabled={retryingOcr || saving || !!selectedFile || !!restoringVersionId}
                        title={selectedFile ? 'Remove the replacement file first, or save it to re-run OCR on the new file' : undefined}
                    >
                        <i className={`fa-solid fa-rotate${retryingOcr ? ' fa-spin' : ''}`} /> {retryingOcr ? 'Re-checking…' : 'Re-check OCR'}
                    </button>
                    {retryError && <small className="text-danger d-block mt-2">{retryError}</small>}
                </div>
            </div>

            {receipt.versions && receipt.versions.length > 0 && (
                <div className="card card-primary card-outline">
                    <div className="card-header">
                        <h5 className="card-title mb-0">Version History</h5>
                    </div>
                    <div className="card-body">
                        <p className="text-muted mb-3" style={{ fontSize: '0.8rem' }}>
                            Every save below is kept permanently. Restoring an older one just makes it
                            active again — nothing is ever deleted.
                        </p>
                        <ul
                            className="list-unstyled mb-0"
                            style={{
                                maxHeight: '420px', overflowY: 'auto', paddingRight: '10px', marginRight: '-10px',
                            }}
                        >
                            {receipt.versions.map((v, idx) => {
                                const isCurrent = v._id === receipt.activeVersionId;
                                // The backend says directly whether activeVersionId got here via
                                // adminRestoreVersion (true) or adminEditReceipt (false) — no need
                                // to infer it from how close two timestamps happen to land.
                                const restored = isCurrent && !!receipt.wasRestored;
                                const prevFileUrl = idx > 0 ? receipt.versions[idx - 1].fileUrl : null;
                                const fileChanged = idx === 0 || v.fileUrl !== prevFileUrl;
                                return (
                                    <li key={v._id} data-testid={`version-row-${v._id}`} className="pb-2 mb-2" style={{ borderBottom: '1px solid var(--grey2)' }}>
                                        <div className="d-flex justify-content-between align-items-start" style={{ gap: '8px' }}>
                                            <div>
                                                <strong>v{idx + 1}</strong>{' '}
                                                {isCurrent && <span className="badge badge-success">Current</span>}
                                                <div className="text-muted" style={{ fontSize: '0.78rem' }}>
                                                    Saved {v.supersededAt && new Date(v.supersededAt).toLocaleString()}
                                                    {v.supersededBy && (
                                                        <> by {[v.supersededBy.name, v.supersededBy.lastName].filter(Boolean).join(' ') || v.supersededBy.email}</>
                                                    )}
                                                </div>
                                                {restored && (
                                                    <div className="text-success" style={{ fontSize: '0.78rem' }}>
                                                        <i className="fa-solid fa-clock-rotate-left" /> Made active again{' '}
                                                        {receipt.lastEditedAt && new Date(receipt.lastEditedAt).toLocaleString()}
                                                        {receipt.lastEditedBy && (
                                                            <> by {[receipt.lastEditedBy.name, receipt.lastEditedBy.lastName].filter(Boolean).join(' ') || receipt.lastEditedBy.email}</>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                            {!isCurrent && (
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-outline-secondary text-nowrap"
                                                    onClick={() => handleRestoreVersion(v._id)}
                                                    disabled={!!restoringVersionId || saving || deciding}
                                                >
                                                    {restoringVersionId === v._id ? 'Restoring…' : 'Restore'}
                                                </button>
                                            )}
                                        </div>
                                        <div className="mt-1" style={{ fontSize: '0.85rem' }}>
                                            {fileChanged && (
                                                <>
                                                    <a href={v.fileUrl} target="_blank" rel="noopener noreferrer" title={v.fileName}>
                                                        {truncateMiddle(v.fileName) || 'file'}
                                                    </a>
                                                    {' — '}
                                                </>
                                            )}
                                            {v.grossTotal != null ? <>Total: {formatDollars(v.grossTotal)}</> : 'No total'}
                                            {v.snapTenderedTotal != null && <> · SNAP: {formatDollars(v.snapTenderedTotal)}</>}
                                            {v.fundingSource && <> · {shortFundingSourceLabel(v.fundingSource)}</>}
                                        </div>
                                    </li>
                                );
                            })}
                            {!receipt.activeVersionId && (
                                <li>
                                    <div>
                                        <strong>v{receipt.versions.length + 1}</strong>{' '}
                                        <span className="badge badge-success">Current</span>
                                    </div>
                                    {(receipt.lastEditedAt || receipt.lastEditedBy) && (
                                        <div className="text-muted" style={{ fontSize: '0.78rem' }}>
                                            Saved {receipt.lastEditedAt && new Date(receipt.lastEditedAt).toLocaleString()}
                                            {receipt.lastEditedBy && (
                                                <> by {[receipt.lastEditedBy.name, receipt.lastEditedBy.lastName].filter(Boolean).join(' ') || receipt.lastEditedBy.email}</>
                                            )}
                                        </div>
                                    )}
                                    <div className="mt-1" style={{ fontSize: '0.85rem' }}>
                                        <a href={receipt.fileUrl} target="_blank" rel="noopener noreferrer" title={receipt.fileName}>
                                            {truncateMiddle(receipt.fileName) || 'file'}
                                        </a>
                                        {' — '}
                                        {receipt.grossTotal != null ? <>Total: {formatDollars(receipt.grossTotal)}</> : 'No total'}
                                        {receipt.snapTenderedTotal != null && <> · SNAP: {formatDollars(receipt.snapTenderedTotal)}</>}
                                        {receipt.fundingSource && <> · {shortFundingSourceLabel(receipt.fundingSource)}</>}
                                    </div>
                                </li>
                            )}
                        </ul>
                        {restoreError && <small className="text-danger d-block mt-2">{restoreError}</small>}
                    </div>
                </div>
            )}
        </div>
    );

    const renderEditCard = () => (
        // Sticky, not just a plain column: the left sidebar grows with every
        // version this receipt ever accumulates (Version History), while
        // this column's own height barely changes — without this, a receipt
        // with a long history leaves Edit Details/Decision stranded at the
        // top with a growing gap of empty space beneath them. Pinning this
        // column means it just stays in view instead, and Approve/Reject
        // (the actual point of this screen) never scrolls out of reach.
        //
        // The sticky rule itself lives in App.css's .edit-loyalty-sticky-col
        // (gated to the lg breakpoint), not inline here — below lg these two
        // columns stack to full width, and an inline style has no way to
        // express "only when side-by-side."
        <div className="col-lg-8 edit-loyalty-sticky-col">
            <div className="card card-primary card-outline">
                <div className="card-header">
                    <h5 className="card-title mb-0">Edit Details</h5>
                </div>
                <div className="card-body">
                    {renderResultAlert()}

                    <MemberSearchField
                        initialMember={receipt.userId}
                        onSelect={handleMemberSelect}
                        disabled={saving}
                    />
                    {/* The Member page (#195): same CircleOne-only rule as
                        the stored details card. A new tab, so unsaved edits
                        here survive; hidden while another member is picked
                        but not saved, since it opens the saved one. */}
                    {showStoredDetails && receipt.userId?._id
                        && (!selectedMember || selectedMember._id === receipt.userId._id) && (
                        <div className="text-right mt-n2 mb-3">
                            <Link
                                to={`/loyaltyMembers/${receipt.userId._id}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="small"
                            >
                                View this member&apos;s receipts and products
                            </Link>
                        </div>
                    )}

                    <div className="form-group row">
                        <label className="col-12 col-md-4 col-form-label">Replace file</label>
                        <div className="col-12 col-md-8">
                            <div className="s_profileupld">
                                <label className="btn-bs-file btn-blue mb-0" style={saving ? { opacity: 0.6, cursor: 'not-allowed' } : undefined}>
                                    <i className="fa-solid fa-upload" /> Choose File
                                    <input
                                        type="file"
                                        aria-label="Replace file"
                                        accept={ACCEPTED_FILE_TYPES.join(',')}
                                        onChange={handleFileSelected}
                                        disabled={saving}
                                    />
                                </label>
                            </div>
                            <small className="text-muted d-block mt-1">
                                Optional — JPEG, PNG, PDF, TIFF, HEIC, or HEIF, up to 20MB. Leave blank to keep the current file.
                            </small>
                            {fileError && <small className="text-danger d-block mt-1">{fileError}</small>}
                        </div>
                    </div>

                    {selectedFile && (
                        <div className="form-group row">
                            <div className="col-12 col-md-8 offset-md-4">
                                <div className="d-flex align-items-center flex-wrap" style={{ gap: '10px' }}>
                                    <span>
                                        <i className="fa-solid fa-paperclip" /> {selectedFile.name}
                                        {' '}
                                        <small className="text-muted">({(selectedFile.size / 1024 / 1024).toFixed(1)} MB)</small>
                                    </span>
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-outline-secondary"
                                        onClick={clearSelectedFile}
                                        disabled={saving}
                                    >
                                        Remove
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    <hr />

                    {OCR_HAS_USABLE_TOTAL.includes(receipt.ocrStatus) && Number.isFinite(receipt.grossTotal) ? (
                        <p className={receipt.ocrStatus === 'not_commissary' ? 'text-warning' : 'text-muted'}>
                            OCR read <strong>{formatDollars(receipt.grossTotal)}</strong> off this receipt — prefilled below.
                            {receipt.ocrStatus === 'not_commissary' && ' No line on this receipt says "COMMISSARY" — choose On base for an Exchange receipt, Off base for any other store.'}
                            {Number.isFinite(receipt.snapTenderedTotal) && (
                                <> It also found <strong>{formatDollars(receipt.snapTenderedTotal)}</strong> tendered via SNAP/EBT.</>
                            )}
                        </p>
                    ) : (
                        <p className="text-muted">
                            {ocrUnavailableReason(receipt.ocrStatus)} — enter (or correct) the amounts below using the receipt image on the left.
                        </p>
                    )}
                    {/* WIC is kept apart from SNAP (somo-circle1-api#799): it earns points like
                        any other payment, so it never goes in the SNAP box below. Shown whether
                        or not OCR read a total, since typing the amounts by hand is when a WIC
                        payment most easily ends up as SNAP. */}
                    {(Number.isFinite(receipt.wicTenderedTotal) || receipt.hasWic) && (
                        <p className="text-warning" data-testid="wic-note">
                            {Number.isFinite(receipt.wicTenderedTotal) ? (
                                <>OCR found <strong>{formatDollars(receipt.wicTenderedTotal)}</strong> paid with WIC, which earns points; don&apos;t enter it as SNAP.</>
                            ) : (
                                <>This receipt shows WIC, which earns points; don&apos;t enter it as SNAP.</>
                            )}
                        </p>
                    )}

                    <div className="form-group row">
                        <label className="col-12 col-md-4 col-form-label">Receipt Total ($)</label>
                        <div className="col-12 col-md-8">
                            <input
                                type="number"
                                min="0"
                                step="0.01"
                                className="form-control"
                                value={grossTotalInput}
                                onChange={(e) => { setGrossTotalInput(e.target.value); setAmountsError(''); setDecideError(''); }}
                                disabled={saving}
                                placeholder="e.g. 47.83"
                            />
                        </div>
                    </div>

                    <div className="form-group row">
                        <label className="col-12 col-md-4 col-form-label">SNAP/EBT Amount ($)</label>
                        <div className="col-12 col-md-8">
                            <input
                                type="number"
                                min="0"
                                step="0.01"
                                className="form-control"
                                value={snapTotalInput}
                                onChange={(e) => { setSnapTotalInput(e.target.value); setAmountsError(''); setDecideError(''); }}
                                disabled={saving}
                                placeholder="0"
                            />
                        </div>
                    </div>

                    <div className="form-group row">
                        <label className="col-12 col-md-4 col-form-label" htmlFor="edit-funding-source">Rewards Funding Source</label>
                        <div className="col-12 col-md-8">
                            <select
                                id="edit-funding-source"
                                className="form-control"
                                value={fundingSourceInput}
                                onChange={(e) => { setFundingSourceInput(e.target.value); setDecideError(''); }}
                                disabled={saving}
                            >
                                <option value="">Not set</option>
                                {fundingSourceOptions.map((f) => (
                                    <option key={f.value} value={f.value}>{f.label}</option>
                                ))}
                            </select>
                            {fundingSourceOptions.length === 0 && (
                                <small className="text-danger d-block mt-1">
                                    No funding sources are currently enabled — enable at least one on Loyalty Program Rules before approving.
                                </small>
                            )}
                        </div>
                    </div>

                    {amountsError && (
                        <div className="form-group row">
                            <div className="col-12 col-md-8 offset-md-4">
                                <small className="text-danger">{amountsError}</small>
                            </div>
                        </div>
                    )}

                    {saveError && (
                        <div className="form-group row">
                            <div className="col-12 col-md-8 offset-md-4">
                                <small className="text-danger">{saveError}</small>
                            </div>
                        </div>
                    )}
                </div>
                <div className="card-footer text-center">
                    <button type="button" className="btn btn-blue" onClick={handleSave} disabled={saving || !selectedMember || !!restoringVersionId}>
                        <i className="fa-solid fa-floppy-disk" /> {saving ? 'Saving…' : 'Save Changes'}
                    </button>
                </div>
            </div>

            <div className="card card-primary card-outline">
                <div className="card-header">
                    <h5 className="card-title mb-0">Decision</h5>
                </div>
                <div className="card-body">
                    <div className="form-group row align-items-center">
                        <label className="col-12 col-md-4 col-form-label">Outcome</label>
                        <div className="col-12 col-md-8">
                            <div className="btn-group" role="group" aria-label="Review outcome">
                                <button
                                    type="button"
                                    className={`btn btn-sm ${reviewType === 'approve' ? 'btn-success' : 'btn-outline-success'}`}
                                    onClick={() => selectReviewType('approve')}
                                >
                                    <i className="fa-solid fa-check" /> Approve
                                </button>
                                <button
                                    type="button"
                                    className={`btn btn-sm ${reviewType === 'reject' ? 'btn-danger' : 'btn-outline-danger'}`}
                                    onClick={() => selectReviewType('reject')}
                                >
                                    <i className="fa-solid fa-xmark" /> Reject
                                </button>
                            </div>
                        </div>
                    </div>

                    {reviewType === 'reject' ? (
                        <div className="form-group row mb-0">
                            <label className="col-12 col-md-4 col-form-label">Reason<br /><small className="text-muted fw-normal">(shown to the member)</small></label>
                            <div className="col-12 col-md-8">
                                <textarea
                                    className="form-control"
                                    rows={3}
                                    value={reason}
                                    onChange={(e) => setReason(e.target.value)}
                                    placeholder="e.g. Duplicate of a previously approved receipt"
                                />
                            </div>
                        </div>
                    ) : (
                        <>
                            <div className="form-group row mb-3">
                                <label className="col-12 col-md-4 col-form-label" htmlFor="edit-earn-location">Bought</label>
                                <div className="col-12 col-md-8">
                                    <select
                                        id="edit-earn-location"
                                        className="form-control"
                                        value={earnLocationInput}
                                        onChange={(e) => { setEarnLocationInput(e.target.value); setDecideError(''); }}
                                    >
                                        <option value="">Choose…</option>
                                        <option value="on_base">On base: Commissary or Exchange{ratePart(earnConfig.earnPercentage)}</option>
                                        <option value="off_base">Off base{ratePart(earnConfig.offBaseEarnPercentage)}</option>
                                    </select>
                                    {receipt.ocrStatus === 'completed' ? (
                                        <small className="text-muted d-block mt-1">OCR matched a Commissary, so this defaults to on base.</small>
                                    ) : (
                                        <small className="text-muted d-block mt-1">OCR didn&apos;t match a Commissary. Check the image: an Exchange receipt is on base.</small>
                                    )}
                                </div>
                            </div>
                            {fundingSourceInput === 'our_pl' && (
                                <div className="form-group row mb-3">
                                    <label className="col-12 col-md-4 col-form-label">Annual Cap</label>
                                    <div className="col-12 col-md-8">
                                        <div className="border rounded p-2 bg-light" data-testid="cap-status-section">
                                            {capLoading && <small className="text-muted">Loading annual cap balance…</small>}
                                            {!capLoading && capStatus && (
                                                <div className="d-flex flex-wrap" style={{ gap: '20px' }}>
                                                    <div>
                                                        <small className="text-muted d-block">Cap for {capPeriodYearLabel(capStatus.capPeriod)}</small>
                                                        <strong>{capStatus.cap.points} pts</strong>
                                                    </div>
                                                    <div>
                                                        <small className="text-muted d-block">Earned so far</small>
                                                        <strong>{capStatus.consumed.points} pts</strong>
                                                    </div>
                                                    <div>
                                                        <small className="text-muted d-block">Available</small>
                                                        <strong className={capStatus.remaining.points <= 0 ? 'text-danger' : 'text-success'}>
                                                            {capStatus.remaining.points} pts
                                                        </strong>
                                                    </div>
                                                </div>
                                            )}
                                            {!capLoading && !capStatus && capError && (
                                                <small className="text-danger">Couldn&apos;t load this member&apos;s annual cap balance.</small>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}
                            {eligibleDollars != null && (
                                <div className="form-group row mb-3">
                                    <label className="col-12 col-md-4 col-form-label">Estimate</label>
                                    <div className="col-12 col-md-8">
                                        <small className="text-muted">
                                            Eligible: ${eligibleDollars.toFixed(2)} {' — '} ~{rawPointsEstimate.toFixed(3)} points
                                            {' '}{earnLocationInput ? '(final amount may be capped)' : '(at the on-base rate until you choose where it was bought)'}
                                        </small>
                                    </div>
                                </div>
                            )}
                            {wouldExceedCap && (
                                <div className="form-group row mb-3">
                                    <div className="col-12 col-md-8 offset-md-4">
                                        <div className="alert alert-warning mb-0" role="alert">
                                            <strong>Annual cap reached.</strong> This member has only{' '}
                                            {Math.max(0, capStatus.remaining.points)} pts left this year — approving as-is will
                                            award {Math.max(0, capStatus.remaining.points)} of the ~{rawPointsEstimate.toFixed(3)} computed
                                            points, unless you override below.
                                        </div>
                                    </div>
                                </div>
                            )}
                            {/* General manual-award override — available any time an admin
                                approves, not just when the cap is exceeded. Lets an admin give
                                more or less than the computed amount, on any funding source.
                                Collapsed behind a button by default so it doesn't crowd the
                                common no-override path. */}
                            <div className="form-group row mb-0">
                                <div className="col-12 col-md-8 offset-md-4">
                                    {!overrideCap ? (
                                        <button type="button" className="btn btn-light border btn-sm" onClick={openOverride}>
                                            <i className="fa-solid fa-plus" /> Adjust points to award
                                        </button>
                                    ) : (
                                        <div className="border rounded p-2">
                                            <div className="d-flex justify-content-between align-items-center mb-2">
                                                <strong>Adjust points to award</strong>
                                                <button type="button" className="btn btn-link btn-sm p-0 text-muted" onClick={closeOverride}>
                                                    Remove override
                                                </button>
                                            </div>
                                            <div className="form-group">
                                                <label htmlFor="override-points">Points to award</label>
                                                <input
                                                    type="number"
                                                    id="override-points"
                                                    className="form-control"
                                                    min="0"
                                                    step="0.001"
                                                    value={overridePoints}
                                                    onChange={(e) => setOverridePoints(e.target.value)}
                                                />
                                            </div>
                                            <div className="form-group mb-0">
                                                <label htmlFor="override-reason">Reason for override (required)</label>
                                                <textarea
                                                    id="override-reason"
                                                    className="form-control"
                                                    rows={2}
                                                    value={overrideReason}
                                                    onChange={(e) => setOverrideReason(e.target.value)}
                                                    placeholder="e.g. Goodwill gesture per support escalation #123"
                                                />
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </>
                    )}

                    {decideError && (
                        <div className="form-group row mb-0">
                            <div className="col-12 col-md-8 offset-md-4">
                                <small className="text-danger">{decideError}</small>
                            </div>
                        </div>
                    )}
                </div>
                <div className="card-footer text-center">
                    <button
                        type="button"
                        className={`btn ${reviewType === 'approve' ? 'btn-success' : 'btn-danger'}`}
                        onClick={handleDecide}
                        disabled={deciding || hasUnsavedFileOrMember || !!restoringVersionId
                            || (reviewType === 'approve' && fundingSourceOptions.length === 0)}
                        title={
                            (restoringVersionId && 'Wait for the version restore to finish first')
                            || (hasUnsavedFileOrMember && 'Save the replacement file / member change first')
                            || undefined
                        }
                    >
                        {deciding ? 'Saving…' : (reviewType === 'approve' ? 'Approve' : 'Reject')}
                    </button>
                    {hasUnsavedFileOrMember && (
                        <div className="mt-2">
                            <small className="text-warning">
                                <i className="fa-solid fa-triangle-exclamation" /> You have an unsaved replacement file or
                                member change — click <strong>Save Changes</strong> above before approving or rejecting.
                            </small>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );

    const renderContent = () => {
        if (loading) {
            return (
                <div className="row">
                    <div className="col-12 text-center py-5">
                        <i className="fa-solid fa-circle-notch fa-spin" style={{ fontSize: '2rem' }} />
                        <p className="text-muted mt-2">Loading receipt…</p>
                    </div>
                </div>
            );
        }
        if (loadError) {
            return (
                <div className="row">
                    <div className="col-12">
                        <div className="alert alert-danger">{loadError}</div>
                    </div>
                </div>
            );
        }
        if (!EDITABLE_STATUSES.includes(receipt.status)) {
            return (
                <div className="row">
                    <div className="col-12">
                        <div className="alert alert-warning">
                            This receipt has already been reviewed and can no longer be edited.
                        </div>
                    </div>
                </div>
            );
        }

        // Stored Receipt Details gets its own row: in the same row as the sticky
        // Edit Details/Decision column, it scrolled up over that column and
        // covered Approve/Reject. A sticky element only pins inside its own
        // row, so ending that row first lets the column scroll away normally.
        return (
            <>
                <div className="row" data-testid="edit-receipt-main-row">
                    {renderPreviewSidebar()}
                    {renderEditCard()}
                </div>
                {showStoredDetails && (
                    <div className="row" data-testid="stored-details-row">
                        <div className="col-12">
                            <div className="card card-primary card-outline">
                                <div className="card-header">
                                    <h5 className="card-title mb-0">Stored Receipt Details</h5>
                                </div>
                                <div className="card-body">
                                    <ReceiptDetails receipt={receipt} />
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </>
        );
    };

    return (
        <div>
            <ToastContainer autoClose={3000} />
            <div className="content-header">
                <div className="container-fluid">
                    <div className="row mb-2 align-items-center">
                        <div className="col-6 col-sm-6">
                            <h1 className="m-0 txt_lft">Edit Loyalty Receipt</h1>
                        </div>
                        <div className="col-6 col-sm-6">
                            <ol className="breadcrumb justify-content-end">
                                <li className="breadcrumb-item">
                                    <Link to="/loyaltyReceipts" state={queueReturnState}><i className="fa-solid fa-arrow-left-long" /> Back to Receipts</Link>
                                </li>
                            </ol>
                        </div>
                    </div>
                </div>
            </div>
            <div className="content">
                <div className="container-fluid">
                    {renderContent()}
                </div>
            </div>

            {/* Full-size image preview — same overlay convention as the old
                Review modal's own image lightbox, generalized to show either
                this receipt's image or the flagged duplicate's. */}
            {previewImage && (
                <div className="modal d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.75)', zIndex: 1060 }}>
                    <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">{previewImage.label}</h5>
                                <button type="button" className="close" onClick={() => setPreviewImage(null)}>
                                    <span>&times;</span>
                                </button>
                            </div>
                            <div className="modal-body text-center">
                                {previewImage.displaySrc ? (
                                    // Already decoded by the sidebar (TIFF) — reuse it.
                                    <img
                                        src={previewImage.displaySrc}
                                        alt={previewImage.label}
                                        style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }}
                                    />
                                ) : isTiffFile(previewImage.url) ? (
                                    modalTiff.status === 'error' ? (
                                        <div className="py-5">
                                            <i className="fa-solid fa-file-image" style={{ fontSize: '3rem', color: 'var(--grey2)' }} />
                                            <p className="text-muted mt-2 mb-0">
                                                Couldn&apos;t render this TIFF here. Use &quot;Open original in new tab&quot; below.
                                            </p>
                                        </div>
                                    ) : modalTiff.status === 'ready' ? (
                                        <img
                                            src={modalTiff.src}
                                            alt={previewImage.label}
                                            style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }}
                                        />
                                    ) : (
                                        <div className="py-5">
                                            <div className="spinner-border text-primary" role="status">
                                                <span className="sr-only">Loading preview...</span>
                                            </div>
                                        </div>
                                    )
                                ) : (
                                    <img
                                        src={previewImage.url}
                                        alt={previewImage.label}
                                        style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }}
                                    />
                                )}
                            </div>
                            <div className="modal-footer">
                                <a href={previewImage.url} target="_blank" rel="noopener noreferrer" className="btn btn-outline-secondary">
                                    Open original in new tab
                                </a>
                                <button type="button" className="btn btn-secondary" onClick={() => setPreviewImage(null)}>
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default EditLoyaltyReceipt;
